import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { useSolanaWallets } from '@privy-io/react-auth/solana';
import { Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { solanaUtils } from '../utils/solanaUtils';

interface WalletContextType {
  // Auth state
  isAuthenticated: boolean;
  isReady: boolean;
  user: any;
  login: () => void;
  logout: () => void;
  
  // Wallet state
  address: string | null;
  balance: number;
  isLoading: boolean;
  solanaWallet: any; // Raw Solana wallet object for transactions
  
  // Validation helpers
  canAfford: (amountSol: number) => boolean;
  getMaxWithdrawable: () => number;
  getAffordableBetSizes: (betSizes: number[]) => boolean[];
  
  // Actions
  refreshBalance: () => Promise<void>;
  
  // Errors
  error: string | null;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

interface WalletProviderProps {
  children: ReactNode;
}

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const { ready, authenticated, user, login, logout } = usePrivy();
  const { wallets } = useSolanaWallets();
  
  // State
  const [balance, setBalance] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Smart wallet selection: prefer the wallet that matches the login method
  const wallet = useMemo(() => {
    if (!wallets || wallets.length === 0) return null;
    
    // If user logged in with wallet connector, prefer external wallets
    const externalWallet = wallets.find(w => 
      w.walletClientType === 'phantom' || 
      w.connectorType === 'solana_adapter' ||
      w.walletClientType !== 'privy'
    );
    
    // If user logged in with Twitter, prefer embedded wallets
    const embeddedWallet = wallets.find(w => w.walletClientType === 'privy');
    
    // Check user's login method to make smart choice
    const hasTwitterLogin = user?.linkedAccounts?.some((account: any) => account.type === 'twitter_oauth');
    
    // Prefer wallet that matches login method
    if (hasTwitterLogin && embeddedWallet) {
      return embeddedWallet;
    } else if (!hasTwitterLogin && externalWallet) {
      return externalWallet;
    }
    
    // Fallback to first available wallet
    return wallets[0];
  }, [wallets, user]);
  
  const address = wallet?.address || null;
  
  // Memoized connection to prevent recreation
  const connection = useMemo(() => 
    new Connection(solanaUtils.getRpcEndpoint(), 'confirmed'), 
    []
  );
  
  // Fetch balance function
  const fetchBalance = useCallback(async () => {
    if (!address) {
      setBalance(0);
      return;
    }
    
    try {
      setError(null);
      const publicKey = new PublicKey(address);
      const lamports = await connection.getBalance(publicKey);
      const solBalance = lamports / LAMPORTS_PER_SOL;
      setBalance(solBalance);
    } catch (error: any) {
      console.error('❌ Failed to fetch balance:', error);
      setError('Failed to fetch balance');
      setBalance(0);
    }
  }, [address, connection]);
  
  // Refresh balance with loading state
  const refreshBalance = useCallback(async () => {
    setIsLoading(true);
    await fetchBalance();
    setIsLoading(false);
  }, [fetchBalance]);
  
  // Initial balance fetch when wallet connects
  useEffect(() => {
    if (authenticated && address) {
      refreshBalance();
    } else {
      // Clear all wallet state when not authenticated
      setBalance(0);
      setError(null);
      setIsLoading(false);
    }
  }, [authenticated, address, refreshBalance]);
  
  // Periodic balance updates (every 3 seconds for trading consistency)
  useEffect(() => {
    if (!authenticated || !address) {
      // Clear interval and reset state when not authenticated
      return;
    }
    
    const interval = setInterval(() => {
      fetchBalance(); // Don't show loading for periodic updates
    }, 3000);
    
    return () => clearInterval(interval);
  }, [authenticated, address, fetchBalance]);
  
  // Validation helpers
  const canAfford = useCallback((amountSol: number): boolean => {
    if (!authenticated || !address || balance <= 0) return false;
    
    // Account for transaction fees (conservative estimate)
    const feeBuffer = 0.002; // ~0.002 SOL for fees
    const availableBalance = balance - feeBuffer;
    
    return availableBalance >= amountSol;
  }, [authenticated, address, balance]);
  
  const getMaxWithdrawable = useCallback((): number => {
    if (!authenticated || !address || balance <= 0) return 0;
    
    // Leave buffer for transaction fees (increased to 0.005 SOL to ensure transactions don't fail)
    // This covers: transaction fee (~0.000005 SOL) + priority fees + safety margin
    const feeBuffer = 0.005;
    const maxWithdrawable = balance - feeBuffer;
    
    return Math.max(0, maxWithdrawable);
  }, [authenticated, address, balance]);
  
  const getAffordableBetSizes = useCallback((betSizes: number[]): boolean[] => {
    return betSizes.map(size => canAfford(size));
  }, [canAfford]);
  
  const contextValue: WalletContextType = {
    // Auth
    isAuthenticated: authenticated,
    isReady: ready,
    user,
    login,
    logout,
    
    // Wallet
    address,
    balance,
    isLoading,
    solanaWallet: wallet, // Raw wallet object for transactions
    
    // Validation
    canAfford,
    getMaxWithdrawable,
    getAffordableBetSizes,
    
    // Actions
    refreshBalance,
    
    // Errors
    error
  };
  
  return (
    <WalletContext.Provider value={contextValue}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = (): WalletContextType => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};